import { useContext, useState } from 'react';
import { ILink } from '../../../interfaces/Link';
import { RightArrow } from '../../../svg';
import Tooltip from '../ToolTip';
import IndicateUp from './IndicateUp';
import FaviconLoader from '../FaviconFetcher';
import { getLinkLabel } from '../../../utils/linkUtils';
import copyToClipboard from '../../../utils/clipboardUtils';
import IndicateDown from './IndicateDown';
import { UserContext } from '../../../context/UserContext';
import { BrandMemberRole } from '../../../types/enums';
import {
  useAddToFavoritesMutation,
  useRemoveFromFavoritesMutation,
} from '../../../hooks/queries/useLinkQueries';
import { toast } from 'react-toastify';
import IconButton from '../IconButton';
import Checkbox from '../Checkbox';
import useI18n from '../../../hooks/useI18n';

interface Props {
  link: ILink;
  isDelete?: boolean;
  editModalOpen?: (val: string) => void;
  detailsModelOpen?: (val: string) => void;
  handleSelectLink?: (val: string) => void;
  shareModalOpen?: (val: string) => void;
  selectedData?: any[];
}

const LinkSquare: React.FC<Props> = ({
  link,
  isDelete,
  editModalOpen,
  detailsModelOpen,
  handleSelectLink,
  shareModalOpen,
  selectedData,
}) => {
  const userContext = useContext(UserContext);
  const { t } = useI18n();
  const [linkLogo, setLinkLogo] = useState('');
  const [isLiked, setIsLiked] = useState(link.isFavorite);
  const clickCountsPercentageNum = Number(link.clickCountsPercentage);
  const safeClickCountsPercentage = Number.isFinite(clickCountsPercentageNum)
    ? clickCountsPercentageNum
    : 0;
  const indicateUp = safeClickCountsPercentage >= 0;

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;
  const addToFavoritesMutation = useAddToFavoritesMutation(user?._id ?? '');
  const removeFromFavoritesMutation = useRemoveFromFavoritesMutation(
    user?._id ?? '',
  );

  const handleClick = (id: string) => {
    editModalOpen?.(id);
  };

  const handleDetailsClick = (id: string) => {
    detailsModelOpen?.(id);
  };

  const handleShareClick = (id: string) => {
    shareModalOpen?.(id);
  };
  const handleLikeClick = async (id: string) => {
    if (!user || !user._id) {
      toast.error(t('toast.userNotFound'));
      return;
    }

    try {
      if (!isLiked) {
        await addToFavoritesMutation.mutateAsync(id);
        setIsLiked(true);
        toast.success(t('toast.addedToFavorites'));
      } else {
        await removeFromFavoritesMutation.mutateAsync(id);
        setIsLiked(false);
        toast.success(t('toast.removedFromFavorites'));
      }
    } catch (error) {
      toast.error(t('toast.failedToUpdateFavorite'));
      console.error('Error updating favorite:', error);
    }
  };
  const handleSelect = (id: string) => {
    handleSelectLink?.(id);
  };

  const handleCopy = (text: ILink) => {
    const link = text.subDomain
      ? `https://${text.subDomain ?? ''}.${text.domain ?? 'linkfluencer.io'}/${text.shortUrl}`
      : `https://${text.domain ?? 'linkfluencer.io'}/${text.shortUrl}`;
    copyToClipboard(link, t('toast.linkCopied'), t('toast.linkCopiedError'));
  };

  const displayForBrandUser = () => {
    return (
      user !== undefined &&
      user !== null &&
      user.brandMemberRole !== BrandMemberRole.User
    );
  };

  const smartLink = link.subDomain
    ? `https://${link.subDomain ?? ''}.${link.domain ?? 'linkfluencer.io'}/${link.shortUrl}`
    : `https://${link.domain ?? 'linkfluencer.io'}/${link.shortUrl}`;

  return (
    <div className="rounded-2xl bg-gray-100 border border-white hover:border-gray-300 hover:shadow-md hover:scale-[1.02] transition-all duration-200 h-full">
      <FaviconLoader
        originalUrl={link.originalUrl}
        setFavicon={setLinkLogo}
        target={link.targetSite}
      />
      <div className="flex justify-between items-center h-[64px] px-[24px]">
        <div className="flex flex-row items-center">
          <img
            src={linkLogo}
            alt={'social Icon'}
            className="mr-3 h-[30px] w-[30px] object-contain"
          />
          <label>{getLinkLabel(link.targetSite)}</label>
        </div>
        {!isDelete ? (
          <div className="flex flex-row items-center gap-2">
            {displayForBrandUser() && (
              <Tooltip
                text={String(t('links.edit'))}
                position="top"
                isCard={true}
              >
                <div className="bg-transparent hover:bg-white rounded-full">
                  <IconButton
                    src="/assets/svgs/fi_edit.svg"
                    className="size-5"
                    effectDarkness={2}
                    onClick={() => handleClick(link._id ?? '0')}
                    variant="custom"
                  />
                </div>
              </Tooltip>
            )}
            <Tooltip
              text={String(t('links.share'))}
              position="top"
              isCard={true}
            >
              <div className="bg-transparent hover:bg-white rounded-full">
                <IconButton
                  src="/assets/svgs/fi_share.svg"
                  className="size-5"
                  effectDarkness={2}
                  onClick={() => handleShareClick(link._id ?? '0')}
                  variant="custom"
                />
              </div>
            </Tooltip>
            {!isLiked ? (
              <Tooltip
                text={String(t('links.addToFavorites'))}
                position="top"
                isCard={true}
              >
                <div className="bg-transparent hover:bg-white rounded-full">
                  <IconButton
                    src="/assets/svgs/fi_heart.svg"
                    className="size-5"
                    effectDarkness={2}
                    onClick={() => handleLikeClick(link._id ?? '0')}
                    variant="custom"
                  />
                </div>
              </Tooltip>
            ) : (
              <Tooltip
                text={String(t('links.removeFromFavorites'))}
                position="top"
                isCard={true}
              >
                <div className="bg-transparent hover:bg-white rounded-full">
                  <IconButton
                    src="/assets/svgs/fi_heart_filled.svg"
                    className="size-5"
                    effectDarkness={2}
                    onClick={() => handleLikeClick(link._id ?? '0')}
                    variant="custom"
                  />
                </div>
              </Tooltip>
            )}
            <Tooltip
              text={String(t('links.copy'))}
              position="top"
              isCard={true}
            >
              <div className="bg-transparent hover:bg-white rounded-full">
                <IconButton
                  src="/assets/svgs/fi_copy.svg"
                  className="size-5"
                  effectDarkness={2}
                  onClick={async () => handleCopy(link)}
                  variant="custom"
                />
              </div>
            </Tooltip>
          </div>
        ) : (
          <Checkbox
            checked={selectedData?.includes(link._id)}
            onClick={() => {
              handleSelect(link._id);
            }}
          />
        )}
      </div>
      <div className="border-x border-b border-[#E5E9EB] p-[24px] flex flex-col justify-between bg-gray-50 rounded-b-2xl h-[calc(100%-64px)]">
        <a
          href={smartLink}
          className="text-[14px] font-content break-words"
          title={link.shortUrl}
          target="_blank"
          rel="noreferrer"
        >
          {smartLink}
        </a>
        <span
          className="text-[12px] font-content break-words mt-2 text-[#9B919D] mb-0"
          title="title"
        >
          {link.metaTitle}
        </span>

        <div className="my-2 text-[#5890FF] text-[14px] flex flex-wrap">
          {link.tags?.length ? (
            link.tags.map((tag, index) =>
              tag.length > 0 ? (
                <span key={index} className="mr-2">
                  {`#${tag}`}
                </span>
              ) : null,
            )
          ) : (
            <span className="text-transparent">-</span>
          )}
        </div>

        <div>
          <span className="text-[12px] text-[#9B919D]">
            {t('links.totalClicked')}
          </span>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-[24px] font-content mr-2">
                {link.clickCount}{' '}
              </h1>
              {indicateUp ? (
                <IndicateUp percent={safeClickCountsPercentage} />
              ) : (
                <IndicateDown percent={Math.abs(safeClickCountsPercentage)} />
              )}
            </div>
            <div>
              <div className="px-2 py-1 rounded-full hover:bg-gray-200 duration-150">
                <Tooltip
                  text={String(t('links.details'))}
                  position="top"
                  isCard={true}
                >
                  <RightArrow
                    className="size-4 cursor-pointer"
                    onClick={() => {
                      handleDetailsClick(link._id ?? '0');
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkSquare;
